
import { EventEmitter, Inject, Injectable} from "@angular/core";

import { Client, WorkerAccount } from "@salaxy/core";

import { ClientNg } from "../api/ClientNg";
import { SessionService } from "../account/SessionService";
import { IRepository } from "./IRepository";

/**
 * Implements the salaxy repository pattern for Workers stored for the current account.
 */
@Injectable()
export class WorkerRepository implements IRepository<WorkerAccount> {

    /** List of items in the repository */
    public list: WorkerAccount[] = [];

    /** Currently selected item in the repository */
    public current: WorkerAccount = null;

    constructor(
        @Inject(ClientNg) private client: Client,
    ) { }

    /**
     * Assures the repository initialization:
     * Initialization is done after successful authentication.
     * Typically the call to fetch the repository list, but may fetch other stuff from the server.
     *
     * @param session - The session service that contains information about the authenticated user.
     */
    public init(session: SessionService) {
        if (session.companyAccount || (session.personAccount && session.personAccount.isEmployer)) {
            this.reloadList();
        }
    }

    /**
     * Reloads the list from the server - called e.g. after delete and add new
     * @param callback - Optional callback that is called after save and susequent reloadList completes.
     */
    public reloadList(callback?: () => any): void {
        this.client.accountsApi.getWorkers().then((workers) => {
            this.list = workers;
            if (callback) {
                callback();
            }
        });
    }

    /**
     * Gets a new blank object with default values, suitable for UI binding.
     * This is a synchronous method - does not go to the server and it is not 100% reliable in that way.
     * However, it shoud provide a basic object, good enough for most views.
     */
    public getBlank(): WorkerAccount {
        // TODO: Go-through and make sensible defaults
        const blank = {
            avatar: {},
            contact: {},
            workerInfo: {
                defaultTaxCard: {
                    forYear: 0,
                },
            },
        };
        return blank;
    }

    /**
     * Sets the current repository item.
     * If not loaded, starts loading from the server to the current.
     * @param id - Identifier of the calculation
     */
    public setCurrentId(id: string) {
        for (const worker of this.list) {
            if (worker.id === id) {
                this.setCurrent(worker);
                return;
            }
        }
        this.setCurrent(null);
    }

    /**
     * Sets the current calculation
     * @param item - Item to set as current
     */
    public setCurrent(item: WorkerAccount) {
        this.current = item;
    }

    /**
     * Creates a new item and sets it as current for editing and saving.
     * The item is not yet sent to server and thus it is not in the list, nor does it have an identifier.
     */
    public newCurrent() {
        this.setCurrent(this.getBlank());
    }

    /**
     * Saves changes to the current item.
     * @param callback - Optional callback that is called after save and susequent reloadList completes.
     */
    public saveCurrent(callback?: () => any): void {
        if (this.current) {
            this.save(this.current, callback);
        } else {
            // console.error("SaveCurrent called when current is not set.");
        }
    }

    /**
     * Adds or updates a given item.
     * Operation is determined based on id: null/''/'new' adds, other string values update.
     * @param item - Item that is updated.
     * @param callback - Optional callback that is called after save and susequent reloadList completes.
     */
    public save(item: WorkerAccount, callback?: () => any): void {
        this.client.accountsApi.updateWorker(item)
        .then((savedObject) => {
            this.setCurrent(savedObject);
            this.reloadList(callback);
        });
    }

    /**
     * Deletes the given item from repository if possible
     * @param id - Identifier of the item to delete
     * @param callback - Optional callback that is called after save and susequent reloadList completes.
     */
    public delete(id: string, callback?: () => any) {
        this.client.accountsApi.deleteWorker(id)
            .then(() => {
                this.reloadList(callback);
            });
    }
}
